import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, model, ModelSignal, output, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FormValueControl } from '@angular/forms/signals';
import { MatIconButton } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';

import { OwlDateTimeInputDirective, OwlDateTimeModule, PickerType } from '@danielmoncada/angular-datetime-picker';

import { BaasI18n } from '@lvflanders/bar';

import { BAR_DATE_TIME_PROVIDERS } from '../../controls/bar-controls.providers';
import { BAAS_DATE_TIME_PICKER_PROVIDER, BaasDateTimePickerProvider } from './baas-date-time-picker-provider';
import { BAAS_DATE_TIME_PICKER_SETTINGS } from './baas-date-time-picker-settings';

export type BaasDateTimePickerType = 'dateTime' | 'date' | 'time';
export type BaasDateTimePickerRangeSelectMode = 'default' | 'from' | 'to';
export type BaasDateTimePickerYearAdjustment = 'none' | 'default' | 'fixedCentury' | 'rollingCentury';

/** Date & time picker for signal forms using the classic & beloved owl date time system. */
@Component({
    selector: 'baas-date-time-picker',
    templateUrl: './baas-date-time-picker.html',
    providers: [...BAR_DATE_TIME_PROVIDERS],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [OwlDateTimeModule, MatIconButton, MatIcon, MatFormFieldModule, MatInput, MatTooltip, FormsModule]
})
export class BaasDateTimePicker implements FormValueControl<Date | null | undefined> {
    readonly #i18n = inject(BaasI18n);
    readonly #provider = inject<BaasDateTimePickerProvider>(BAAS_DATE_TIME_PICKER_PROVIDER);
    readonly #settings = inject(BAAS_DATE_TIME_PICKER_SETTINGS);

    readonly input = viewChild.required<ElementRef<HTMLInputElement>>('input');
    protected readonly dateTimeInput = viewChild.required(OwlDateTimeInputDirective);
    // NOTE typing needed for d.ts generation, otherwise it becomes of type Date
    readonly value: ModelSignal<Date | null | undefined> = model<Date | null | undefined>(undefined);
    /** Id used to associate with label for. */
    readonly labelForId = input<string>();
    /** Label text. If left out no label is used. */
    readonly label = input<string>();
    /** Wether to wrap the label over multiple lines. */
    readonly wrapLabel = input<boolean>(false);
    /** Tooltip for the label. */
    readonly labelTooltip = input<string>();
    /** DateTime picker type. */
    readonly pickerType = input<BaasDateTimePickerType>('date');
    /** Min selectable date. */
    readonly minDate = input<Date | undefined>();
    /** Max selectable date. */
    readonly maxDate = input<Date | undefined>();
    /** Start date at which to start the calendar. */
    readonly startAt = input<Date | undefined>();
    /** Whether a range should be selected. */
    readonly range = input(false);
    /** Which kind of range select mode in case range is true. */
    readonly rangeSelectMode = input<BaasDateTimePickerRangeSelectMode>('default');
    readonly readonly = input(false);
    readonly required = input<boolean>();
    readonly disabled = input<boolean>();
    readonly includeSeconds = input(false);
    /** Allows to filter specific dates. */
    readonly dateTimeFilter = input<(date: Date) => boolean>();
    /** Ensures that the input textbox always contains a valid value on blur. If the form control value is null the input is emptied. */
    readonly ensureValidInput = input(true);
    /** Automatically adjusts the year in case less than 3 digits. */
    readonly yearAdjustment = input<BaasDateTimePickerYearAdjustment | boolean>(undefined);
    /** Shortcut callback allowing to set a given Date when a keyboard event occurs. */
    readonly shortcutHandler = input<(event: KeyboardEvent) => Date | undefined | null>();
    /**
     * Defines when the value is updated when value entered through input with ngModel.
     * Note does not work with formControl or formControlName. In this case you need to use the FormControl options updateOn.
     */
    /** Occurs on input blur. */
    // eslint-disable-next-line @angular-eslint/no-output-native
    readonly blur = output();
    readonly changed = output<any>();

    protected readonly resolvedPickerType = computed<PickerType>(() => {
        switch (this.pickerType()) {
            case 'dateTime':
                return 'both';
            case 'time':
                return 'timer';
            default:
                return 'calendar';
        }
    });
    protected readonly selectionTitle = computed(() => {
        switch (this.pickerType()) {
            case 'dateTime':
                return this.#i18n.get('DOMAIN_BAR_CONTROLS.DATE_TIME_PICKER.SELECTION_TITLE.DATE_TIME');
            case 'time':
                return this.#i18n.get('DOMAIN_BAR_CONTROLS.DATE_TIME_PICKER.SELECTION_TITLE.TIME');
            default:
                return this.#i18n.get('DOMAIN_BAR_CONTROLS.DATE_TIME_PICKER.SELECTION_TITLE.DATE');
        }
    });
    protected readonly selectMode = computed(() => {
        if (!this.range()) return 'single';
        else {
            switch (this.rangeSelectMode()) {
                case 'from':
                    return 'rangeFrom';
                case 'to':
                    return 'rangeTo';
                default:
                    return 'range';
            }
        }
    });

    readonly #yearAdjustment = computed(() => {
        switch (this.yearAdjustment()) {
            case 'default':
            case 'rollingCentury':
            case true:
                return 'rollingCentury';
            case false:
                return 'none';
            case 'fixedCentury':
                return 'fixedCentury';
            default:
                return 'none';
        }
    });

    focus(): void {
        this.input()?.nativeElement?.focus();
    }

    protected onKeyDown(event: KeyboardEvent): void {
        let result: Date = undefined;
        if (this.shortcutHandler()) {
            result = this.shortcutHandler()(event);
        } else if (this.#provider.handleShortcut) {
            result = this.#provider.handleShortcut(event);
        }
        if (result !== undefined) {
            // NOTE fix value was always empties on blur in case not debugging
            requestAnimationFrame(() => this.value.set(result));
        }
    }

    protected onDateTimeChange(date: Date) {
        this.changed.emit(date);
    }

    protected onDateTimeInput(event: { value: Date | null; input: HTMLInputElement }): void {
        if (
            this.#settings.reformatInputOnAccept &&
            this.pickerType() === 'date' &&
            !this.range() &&
            event.value instanceof Date &&
            /^\d{8}$/.test(event.input.value)
        ) {
            this.dateTimeInput().formatNativeInputValue();
        }
    }

    protected onBlur(): void {
        if (this.ensureValidInput() && !this.value() && this.input()?.nativeElement?.value && this.input().nativeElement.value.length > 0) {
            this.input().nativeElement.value = null;
        } else if (this.#yearAdjustment() !== 'none') {
            const date = this.value();
            if (date instanceof Date && date.getFullYear() < 100) {
                const adjustedYear = this.#adjustYear(date.getFullYear());
                this.value.set(new Date(date.setFullYear(adjustedYear)));
            }
        }

        if (this.#settings.reformatInputOnAccept && this.value()) {
            this.dateTimeInput().formatNativeInputValue();
        }

        this.blur.emit();
    }

    #adjustYear(year: number): number {
        if (year < 100) {
            const currentYear = new Date().getFullYear();
            const currentCentury = Math.floor(currentYear / 100) * 100;

            switch (this.#yearAdjustment()) {
                case 'rollingCentury': {
                    const lastTwoDigits = currentYear % 100; // Extract last two digits of the current year
                    if (year < 50) {
                        return lastTwoDigits < 50 ? currentCentury + year : currentCentury + 100 + year;
                    } else {
                        return lastTwoDigits < 50 ? currentCentury - 100 + year : currentCentury + year;
                    }
                }
                case 'fixedCentury':
                    return currentCentury + year;
            }
        }
        return year;
    }
}
